import store from "@/vuex/store";

let registeredComponents = [];

/**
 * Intended to be used instead of $(document).ready(..).
 *
 * The callback will be fired on initial page load and any other subsequent
 * turbo visits. $(document).ready(...) only fires on the initial page load.
 *
 * The callback passed here will fire on every page load so ensure that the
 * callback is written in a way where this will not cause issues.
 *
 * @param {Function} callback Callback to fire
 */
window.readyListeners = [];

export const onReady = (callback) => {
  $(document).ready(callback);
  $(document).on("turbo:render", callback);
  window.readyListeners.push(callback);
};

export const triggerOnReady = () => {
  window.readyListeners.forEach((fn) => fn.call(this));
};

/**
 * Register a Vue component with turbo. This will trigger a destroy on the
 * component passed before turbo navigates to a new page.
 *
 * @param {Object} component Vue component
 */
export const registerVueComponent = (component) => {
  registeredComponents.push(component);
};

/**
 * Initializes turbo navigation events to do the following:
 *
 * 1. Destroy all registered components and reset the Vuex store before navigating to a new page.
 * 2. Mark in-app navigation pages as needing a refresh when navigating away.
 * 3. Refresh pages marked as needing a refresh when using back/forward button.
 * 4. Catch abort errors when in-progress requests are cancelled (navigating away).
 */
export const initTurboEvents = () => {
  // Add a custom header to identify turbo requests
  $(document).on("turbo:before-fetch-request", (e) => {
    const event = e.originalEvent;
    event.preventDefault();
    event.detail.fetchOptions.headers["Turbo-Drive"] = "true";
    event.detail.resume();
  });

  // Destroy all registered components and reset the Vuex store before each render.
  $(document).on("turbo:before-render", (e) => {
    const event = e.originalEvent;
    event.preventDefault();

    // registeredComponents.forEach((component) => {
    // component.unmount();
    // });
    registeredComponents = [];

    store.commit("reset");
    event.detail.resume();
  });

  // Check before a turbo visit if the previous page was in-app navigation.
  // If so, mark it to refresh if the user hits the back button.
  $(document).on("turbo:before-visit", () => {
    if (history.state?.turbo === undefined) {
      const state = { ...history.state, turboRefresh: true };
      history.replaceState(state, "");
    }
  });

  // Only show turbo frame loader on initial load
  $(document).on("turbo:frame-load", (e) => {
    const frame = e.target;
    const container = frame.closest(".initial-load");
    if (!container) return;

    container.classList.remove("initial-load");
  });

  // Reinitialize Parsley validation after turbo stream replace action
  // This happens when forms are re-rendered after validation errors
  document.addEventListener("turbo:before-stream-render", (e) => {
    const { render } = e.detail;

    // Store original render function
    const originalRender = render;

    // Override render to add callback after replacement
    e.detail.render = function (streamElement) {
      originalRender(streamElement);

      // After replacement, reinitialize Parsley on any forms
      setTimeout(() => {
        const $forms = $(".parsley-validations");
        if ($forms.length) {
          $forms.each(function () {
            // Destroy existing parsley instance if it exists
            const parsleyInstance = $(this).parsley();
            if (parsleyInstance) {
              parsleyInstance.destroy();
            }

            // Reinitialize parsley
            $(this).parsley({
              excluded:
                "input[type=button], input[type=submit], input[type=reset], input[type=hidden], [disabled], :hidden",
              successClass: "parsley-success-input"
            });
          });
        }
      }, 0);
    };
  });

  // Trigger a turbo refresh if the page being navigated to requires it.
  $(window).on("popstate", (e) => {
    const { state } = e.originalEvent;
    if (state?.turboRefresh) {
      window.Turbo.visit(window.location.href, { action: "replace" });
    }
  });

  // Catch Abort errors when Turbo requests are cancelled (navigating away)
  $(window).on("unhandledrejection", (e) => {
    const event = e.originalEvent;
    if (event.reason?.name === "AbortError") {
      event.preventDefault();
    }
  });
};

/**
 * Returns the state params to use for turbo when updating the URL through pushState or replaceState.
 *
 * @param {Object} currentState The state of the current history entry
 * @param {Boolean} replace If the current history entry is being replaced
 *
 * @returns {Object} State object
 */
export const turboState = (currentState, replace) => {
  const state = {};
  if (!replace) return state;

  // The turboRefresh state param causes the URL to be refreshed by turbo when navigating back/foward.
  // If the URL being replaced was loaded by turbo or if turboRefresh was already set, set turboRefresh.
  // See the popstate handler in initTurboEvents for details.
  if (currentState.turbo || currentState.turboRefresh) {
    state.turboRefresh = true;
  }

  return state;
};
